import { useState, useEffect } from "react";
import axios from "axios";
import { 
  ChevronRight, 
  ChevronDown, 
  Scale, 
  ShieldAlert, 
  BookOpen,
  AlertTriangle,
  DollarSign
} from "lucide-react";
import { API } from "@/utils/constants";

const ViolationTypeLabel = {
  'Overtime': 'Overtime',
  'DoubleTime': 'Double Time',
  'MealBreak': 'Meal Break',
  'RestBreak': 'Rest Break'
};

const ViolationCard = ({ violation }) => {
  const [showStatute, setShowStatute] = useState(false);
  
  if (!violation.is_applicable) {
    return (
      <div className="p-3 rounded-lg bg-slate-900/30 border border-slate-700/50">
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-500">
            {ViolationTypeLabel[violation.violation_type] || violation.violation_type}
          </span>
          <span className="text-xs text-slate-600 italic">Not applicable</span>
        </div>
      </div>
    );
  }
  
  return (
    <div className="rounded-lg bg-red-950/20 border border-red-900/30 overflow-hidden">
      {/* Violation Header */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-red-400" />
            <span className="font-medium text-red-300">
              {ViolationTypeLabel[violation.violation_type] || violation.violation_type}
            </span>
          </div>
          {violation.statute_of_limitations_years > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-900/50 text-blue-300">
              {violation.statute_of_limitations_years}yr statute
            </span>
          )}
        </div>
        
        {/* Penalties Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <div className="p-2 rounded bg-slate-800/50">
            <p className="text-[10px] uppercase text-slate-500 mb-1">Back Wages</p>
            <p className="text-sm font-bold text-red-300">{violation.wage_multiplier}x</p>
          </div>
          <div className="p-2 rounded bg-slate-800/50">
            <p className="text-[10px] uppercase text-slate-500 mb-1">Liquidated</p>
            <p className="text-sm font-bold text-purple-300">
              {violation.liquidated_multiplier > 0 ? `${violation.liquidated_multiplier}x` : 'N/A'}
            </p>
          </div>
          <div className="p-2 rounded bg-slate-800/50 col-span-2">
            <p className="text-[10px] uppercase text-slate-500 mb-1">Civil Penalty / Violation</p>
            <p className="text-sm font-bold text-orange-300">
              {violation.civil_penalty_max > 0 
                ? `$${violation.civil_penalty_min.toLocaleString()} - $${violation.civil_penalty_max.toLocaleString()}`
                : 'N/A'
              }
            </p>
          </div>
        </div>
        
        {/* Formula Description */}
        {violation.formula_description && (
          <p className="text-xs text-slate-400 leading-relaxed">
            {violation.formula_description}
          </p>
        )}
      </div>
      
      {/* Statute Section - Expandable */}
      {(violation.statute_reference || violation.statute_summary) && (
        <div className="border-t border-red-900/30">
          <button
            onClick={() => setShowStatute(!showStatute)}
            className="w-full flex items-center justify-between px-4 py-2 hover:bg-red-900/20 transition-colors"
          >
            <div className="flex items-center gap-2">
              <BookOpen className="w-3.5 h-3.5 text-blue-400" />
              <span className="text-xs text-blue-400 font-medium">
                {showStatute ? 'Hide' : 'View'} Legal Statute
              </span>
            </div>
            {showStatute ? (
              <ChevronDown className="w-3.5 h-3.5 text-blue-400" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5 text-blue-400" />
            )}
          </button>
          
          {showStatute && (
            <div className="px-4 pb-4 space-y-3">
              {/* Citation */}
              {violation.statute_reference && (
                <div>
                  <p className="text-[10px] uppercase text-blue-400 mb-1">Legal Citation</p>
                  <p className="text-sm text-white font-mono bg-slate-800 px-3 py-2 rounded">
                    {violation.statute_reference}
                  </p>
                </div>
              )}
              
              {/* Summary */}
              {violation.statute_summary && (
                <div>
                  <p className="text-[10px] uppercase text-blue-400 mb-1">Summary</p>
                  <p className="text-sm text-slate-300 leading-relaxed">
                    {violation.statute_summary}
                  </p>
                </div>
              )}
              
              {/* Full Text - Expandable */}
              {violation.statute_verbose && (
                <StatuteVerboseSection text={violation.statute_verbose} />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StatuteVerboseSection = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-400 transition-colors mb-2"
      >
        <span>{expanded ? 'Hide' : 'Show'} Full Legal Text</span>
        {expanded ? (
          <ChevronDown className="w-3 h-3" />
        ) : (
          <ChevronRight className="w-3 h-3" />
        )}
      </button>
      {expanded && (
        <div className="p-3 rounded bg-slate-900 border border-slate-700 max-h-48 overflow-y-auto">
          <p className="text-xs text-slate-400 whitespace-pre-wrap leading-relaxed">
            {text}
          </p>
        </div>
      )}
    </div>
  );
};

const StateLaws = () => {
  const [laws, setLaws] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedState, setExpandedState] = useState(null);

  useEffect(() => {
    const fetchLaws = async () => {
      try {
        const response = await axios.get(`${API}/state-laws`);
        setLaws(response.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchLaws();
  }, []);

  return (
    <div data-testid="state-laws-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white font-['Manrope']">State Wage & Hour Laws</h1>
        <p className="text-sm text-slate-400 mt-1">Reference guide for state labor law requirements and violation penalties</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2EC4B6]"></div>
        </div>
      ) : laws.length === 0 ? (
        <div className="bg-slate-800/80 border border-slate-700 rounded-lg p-8 text-center text-slate-400">
          <Scale className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p>No state law data available</p>
        </div>
      ) : (
        <div className="space-y-4">
          {laws.map((law) => (
            <div 
              key={law.state}
              data-testid={`state-law-${law.state.toLowerCase().replace(/\s+/g, '-')}`}
              className="bg-slate-800/80 border border-slate-700 rounded-xl shadow-sm overflow-hidden"
            >
              <button
                onClick={() => setExpandedState(expandedState === law.state ? null : law.state)}
                className="w-full flex items-center justify-between p-5 hover:bg-slate-700/50 transition-colors"
                data-testid={`toggle-${law.state.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#2EC4B6]/30 to-[#1B3A6B]/30 flex items-center justify-center">
                    <Scale className="w-6 h-6 text-[#2EC4B6]" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-white text-lg">{law.state}</h3>
                    <div className="flex items-center gap-3 mt-1">
                      {law.minimum_wage_amount && (
                        <span className="text-sm text-emerald-400 flex items-center gap-1">
                          <DollarSign className="w-3.5 h-3.5" />
                          {law.minimum_wage_amount.toFixed(2)}/hr
                        </span>
                      )}
                      {law.violations && law.violations.filter(v => v.is_applicable).length > 0 && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-red-900/50 text-red-300 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          {law.violations.filter(v => v.is_applicable).length} violation types
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {expandedState === law.state ? (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                )}
              </button>

              {expandedState === law.state && (
                <div className="border-t border-slate-700 p-5 space-y-6">
                  {/* Wage Laws Section */}
                  <div>
                    <h4 className="text-sm font-bold text-white uppercase tracking-wide mb-4 flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-emerald-400" />
                      Wage & Hour Requirements
                    </h4>
                    
                    {law.minimum_wage_comments && (
                      <div className="mb-4">
                        <p className="text-xs font-semibold text-emerald-400 mb-2">Minimum Wage Details</p>
                        <p className="text-sm text-slate-300 whitespace-pre-wrap bg-emerald-950/20 p-3 rounded-lg border border-emerald-800/30">
                          {law.minimum_wage_comments}
                        </p>
                      </div>
                    )}

                    <div className="grid md:grid-cols-2 gap-4">
                      {law.daily_overtime && (
                        <div>
                          <p className="text-xs font-semibold text-amber-400 mb-2">Daily Overtime</p>
                          <p className="text-sm text-slate-300 whitespace-pre-wrap bg-amber-950/20 p-3 rounded-lg border border-amber-800/30">
                            {law.daily_overtime}
                          </p>
                        </div>
                      )}

                      {law.weekly_overtime && (
                        <div>
                          <p className="text-xs font-semibold text-amber-400 mb-2">Weekly Overtime</p>
                          <p className="text-sm text-slate-300 whitespace-pre-wrap bg-amber-950/20 p-3 rounded-lg border border-amber-800/30">
                            {law.weekly_overtime}
                          </p>
                        </div>
                      )}

                      {law.meal_break_law && (
                        <div>
                          <p className="text-xs font-semibold text-blue-400 mb-2">Meal Break Requirements</p>
                          <p className="text-sm text-slate-300 whitespace-pre-wrap bg-blue-950/20 p-3 rounded-lg border border-blue-800/30">
                            {law.meal_break_law}
                          </p>
                        </div>
                      )}

                      {law.rest_break_law && (
                        <div>
                          <p className="text-xs font-semibold text-blue-400 mb-2">Rest Break Requirements</p>
                          <p className="text-sm text-slate-300 whitespace-pre-wrap bg-blue-950/20 p-3 rounded-lg border border-blue-800/30">
                            {law.rest_break_law}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Violation Penalties Section */}
                  {law.violations && law.violations.length > 0 && (
                    <div>
                      <h4 className="text-sm font-bold text-white uppercase tracking-wide mb-4 flex items-center gap-2">
                        <ShieldAlert className="w-4 h-4 text-red-400" />
                        Violation Penalties & Statutes
                      </h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        {law.violations.map((violation) => (
                          <ViolationCard key={`${law.state_code}-${violation.violation_type}`} violation={violation} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StateLaws;
